/** Formatting helpers. Amounts are kept as decimal strings end-to-end; never float math on money. */
import { currentLocale } from '../i18n';

/**
 * Format a fiat decimal string for the active locale. The string is handed to Intl as-is
 * (modern engines format decimal strings exactly); Number() is only a fallback for display.
 */
export function formatFiat(amount: string, currency: string, locale = currentLocale()): string {
  const clean = amount.trim();
  if (!/^-?\d+(\.\d+)?$/.test(clean)) return `${amount} ${currency}`;
  try {
    const nf = new Intl.NumberFormat(locale, { style: 'currency', currency });
    try {
      return nf.format(clean as unknown as number);
    } catch {
      return nf.format(Number(clean));
    }
  } catch {
    return `${clean} ${currency}`;
  }
}

/** Locale-aware integer (confirmation counts etc.). */
export function formatInt(n: number | string, locale = currentLocale()): string {
  try {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number(n));
  } catch {
    return String(n);
  }
}

/** Locale-aware date-time for "as of" style hints. */
export function formatDateTime(iso: string, locale = currentLocale()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

/** Trim trailing zeros from a decimal string, keeping at least one digit after the point if any. */
export function trimDecimal(s: string): string {
  if (!s.includes('.')) return s;
  return s.replace(/\.?0+$/, '');
}

export function shortAddress(addr: string, head = 6, tail = 6): string {
  if (addr.length <= head + tail + 3) return addr;
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
}

export function shortHash(h: string): string {
  return shortAddress(h, 8, 6);
}

/** Convert a human decimal string to minor units as BigInt, exactly. */
export function toMinor(amount: string, decimals: number): bigint {
  const [whole, frac = ''] = amount.split('.');
  const fracPadded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  return BigInt(whole || '0') * 10n ** BigInt(decimals) + BigInt(fracPadded || '0');
}

/** Convert minor units (BigInt or decimal string) to a human decimal string. */
export function fromMinor(minor: bigint | string, decimals: number): string {
  const v = typeof minor === 'string' ? BigInt(minor) : minor;
  const neg = v < 0n;
  const abs = neg ? -v : v;
  const s = abs.toString().padStart(decimals + 1, '0');
  const whole = s.slice(0, s.length - decimals);
  const frac = s.slice(s.length - decimals);
  const out = decimals ? trimDecimal(`${whole}.${frac}`) : whole;
  return neg ? `-${out}` : out;
}

/** Countdown digits in the active locale's numbering system, two digits each. */
export function mmss(ms: number, locale = currentLocale()): { m: string; s: string } {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  try {
    const nf = new Intl.NumberFormat(locale, { minimumIntegerDigits: 2, useGrouping: false });
    return { m: nf.format(m), s: nf.format(s) };
  } catch {
    return { m: String(m).padStart(2, '0'), s: String(s).padStart(2, '0') };
  }
}

export function isMobile(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/** Stable key for a payment option: the canonical `<asset>:<chain>` id already encodes the chain. */
export function optKey(o: { asset: string }): string {
  return o.asset;
}
